import React, { JSX, PureComponent } from "react";
import { RoleRestricted, Roles } from "@app/user";
import { Accordion, AccordionContainer, formatUnconvertedDateCustom } from "@app/shared";
import { valueOrDashes } from "@app/shared/utility";
import { QESAlert } from "@app/shared/qes-standards";
import { Waffles } from "@app/shared/waffles";
import { ColumnConfig, TextAlignment, wafflesKeyColumnName, WafflesThemes } from "@app/shared/waffles/exports";
import { ProjectOptions } from "@app/projects";
import { NetworkAssessmentAnalysisType, NPIStatus, ProviderNetworkType } from "@app/projects/enumerators";
import { ProjectRunConfigurationResult } from "@app/projects/projectRunConfigurationResult";
import { ClaimsProviderInsights } from "@app/projects/servicing-provider";
import { getEntityIconClasses, getEntityTypeDescription } from "@app/projects/servicing-provider/ServicingProviderHelpers";
import { ServicingProviderDetail } from "@app/projects/results/servicing-provider";
import { PciDashboardProvider } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardProvider";
import { PciDashboardProviderPayload } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardProviderPayload";
import { PciDashboardService } from "@app/projects/network-assessment/pci-dashboard/shared";
import { Features } from "@app/feature-flags";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";

type PciDashboardProviderProfileProps = FeatureFlagContextProps & {
  isProjected?: boolean;
  isTerminated?: boolean;
  netNewProvider?: boolean;
  nextClosestProvider?: boolean;
  project: ProjectOptions;
  providerId: number;
  providerName: string;
  runConfiguration: ProjectRunConfigurationResult;
  providerNetworkType: ProviderNetworkType;
  specialtyGroupCode: string;
  specialtyGroupName: string;
  countySSA: string,
  npi: string
}

interface PciDashboardProviderProfileState {
  record: PciDashboardProvider;
  addressTableData: PciDashboardFlyoutAddressTableData[];
  carejourneyTableData: PciDashboardFlyoutCareJourneyConditionTableData[];
}

interface PciDashboardFlyoutAddressTableData {
  associatedAddress: string | JSX.Element;
  county: string | JSX.Element;
  countySSA: string | JSX.Element;
  latitude: string | JSX.Element;
  longitude: string | JSX.Element;
  [wafflesKeyColumnName]: string;
}

interface PciDashboardFlyoutCareJourneyConditionTableData {
  costScore: string | JSX.Element;
  episodeName: string | JSX.Element;
  numberOfEpisodes: string | JSX.Element;
  qualityScore: string | JSX.Element;
  [wafflesKeyColumnName]: string;
}

const addressColumnConfig: ColumnConfig = {
  associatedAddress: {
    alignment: TextAlignment.Left,
    displayText: "Associated Addresses",
    noSort: true,
    testId: "pci-flyout-addresses",
  },
  county: {
    alignment: TextAlignment.Left,
    displayText: "County",
    noSort: true,
    testId: "pci-flyout-county",
  },
  countySSA: {
    alignment: TextAlignment.Left,
    displayText: "County SSA",
    noSort: true,
    testId: "pci-flyout-county-ssa",
  },
  latitude: {
    alignment: TextAlignment.Left,
    displayText: "Latitude",
    noSort: true,
    testId: "pci-flyout-latitude",
  },
  longitude: {
    alignment: TextAlignment.Left,
    displayText: "Longitude",
    noSort: true,
    testId: "pci-flyout-longitude",
  }
};

const carejourneyColumnConfig: ColumnConfig = {
  /* eslint-disable sort-keys */
  episodeName: {
    alignment: TextAlignment.Left,
    displayText: "Episode Name",
    noSort: true,
    testId: "pci-flyout-episode-name",
  },
  numberOfEpisodes: {
    alignment: TextAlignment.Left,
    displayText: "Number of Episodes",
    noSort: true,
    testId: "pci-flyout-number-of-episodes",
  },
  costScore: {
    alignment: TextAlignment.Left,
    displayText: "Cost Score",
    noSort: true,
    testId: "pci-flyout-cost-score",
  },
  qualityScore: {
    alignment: TextAlignment.Left,
    displayText: "Quality Score",
    noSort: true,
    testId: "pci-flyout-quality-score",
  }
  /* eslint-enable sort-keys */
};

class WrappedPciDashboardProviderFlyout extends PureComponent<PciDashboardProviderProfileProps, PciDashboardProviderProfileState> {
  private readonly pciDashboardService = new PciDashboardService();
  constructor(props: PciDashboardProviderProfileProps) {
    super(props);
    this.state = {
      addressTableData: [],
      carejourneyTableData: [],
      record: null
    };
  }

  public async componentDidMount() {
    const providerPayload: PciDashboardProviderPayload = {
      countySSA: this.props.countySSA,
      networkAssessmentAnalysisType: this.props.runConfiguration.networkAssessmentAnalysisType || NetworkAssessmentAnalysisType.ServiceArea,
      npi: this.props.npi,
      specialtyGroupCode: this.props.specialtyGroupCode
    }

    const record = await this.pciDashboardService.getServicingProviderDetail(
      this.props.project.projectId,
      this.props.runConfiguration.projectRunConfigurationId,
      this.props.providerNetworkType,
      this.props.providerId,
      providerPayload);

    if (record.providerId === 0) {
      this.createDefaultRecord(record);
    }

    const addressTableData = record.addresses.map((address, index) => {
      const row: PciDashboardFlyoutAddressTableData = {
        /* eslint-disable sort-keys */
        associatedAddress: <>{valueOrDashes(address.address)} {!!address.address2 ? <><br />{address.address2}</> : null} <br /> {address.city}{!!address.state ? <>, {address.state} {address.zip}</> : null }</>,
        county: <>{valueOrDashes(address.countyName)}</>,
        countySSA: <>{valueOrDashes(address.countySSA)}</>,
        longitude: <>{valueOrDashes(address.latitude)}</>,
        latitude: <>{valueOrDashes(address.longitude)}</>,
        key: index.toString()
        /* eslint-enable sort-keys */
      }
      return row;
    });

    const carejourneyTableData = record.careJourneyScoresTopEpisodes.map((episode, index) => {
      const row: PciDashboardFlyoutCareJourneyConditionTableData = {
        /* eslint-disable sort-keys */
        episodeName: <>{valueOrDashes(episode.name)}</>,
        numberOfEpisodes: <>{valueOrDashes(episode.count)}</>,
        costScore: <>{valueOrDashes(episode.costScore)}/5</>,
        qualityScore: <>{valueOrDashes(episode.qualityScore)}/5</>,
        key: index.toString()
        /* eslint-enable sort-keys */
      }
      return row;
    });

    this.setState((prevState) => ({
      ...prevState,
      addressTableData: addressTableData,
      carejourneyTableData: carejourneyTableData,
      record: record
    }));
  }

  public render() {
    const record = this.state && this.state.record
      ? this.state.record
      : null;

    return (
      <>
        {(!record || null) && (this.renderLoading())}
        {(record || null) && (this.renderProfile(record))}
      </>
    );
  }

  private renderLoading() {
    return (
      <div className="row">
        <div className="col-12 text-center">
          <span>
            <i className="fa fa-spinner fa-spin" /> Loading additional information for <strong>{this.props.providerName}</strong>...
          </span>
        </div>
      </div>
    );
  }

  private renderProfile(record: PciDashboardProvider) {
    const showAttestation = this.props.providerNetworkType !== ProviderNetworkType.Market && this.props.project.showAttestation;

    return (
      <div className="servicing-provider-profile">
        <section className="intro">
          <div className="row">
            <div className="col-sm-3 mb-3">
              <i className={getEntityIconClasses(record.entityType)} />
            </div>

            <div className="col-sm-9 mb-3">
              <h1 className="pl0-imp">
                {record.displayName}
                {record.credentials ? `, ${record.credentials}` : ""}
              </h1>
              {record.entityType ?
                <div>
                  <strong className="text-muted">{getEntityTypeDescription(record.entityType)}</strong>
                  <br />
                </div> :
                null
              }
              {record.addresses[0]?.address ? <>{record.addresses[0]?.address}<br /></> : null}
              {record.addresses[0]?.address2 ? <>{record.addresses[0]?.address2}<br /></> : null}
              {record.addresses[0]?.city}
              {record.addresses[0]?.city && (record.addresses[0]?.state || record.addresses[0]?.zip) ? ", " : null}
              {record.addresses[0]?.state} {record.addresses[0]?.zip}<br />
              <strong>NPI Status</strong> {record.npiStatus ? this.renderStatus(record.npiStatus) : null}
              {this.renderAttestationStatus(showAttestation, record.lastAttestedDate)}
            </div>
          </div>
        </section>

        <AccordionContainer>
          <Accordion title="General" defaultOpen={true} testId="pci-dashboard-provider-profile__general-accordion">
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">NPI</div>
              <div className="data-box-content">
                {valueOrDashes(record.npi)}
              </div>
            </div>
            <RoleRestricted allowedRoles={[Roles.TIN_VIEWER]}>
              <div className="data-box col-12 col-sm-6 col-md-4">
                <div className="data-box-header">TIN</div>
                <div className="data-box-content">
                  {valueOrDashes(record.tin)}
                </div>
              </div>
            </RoleRestricted>
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">First</div>
              <div className="data-box-content">
                {valueOrDashes(record.firstName)}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">Middle</div>
              <div className="data-box-content">
                {valueOrDashes(record.middleName)}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">Last</div>
              <div className="data-box-content">
                {valueOrDashes(record.lastName)}
              </div>
            </div>

            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">Taxonomy</div>
              <div className="data-box-content">
                {valueOrDashes(record.taxonomyCode)}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">Specialt{record.specialtiesDisplay.length > 1 ? "ies" : "y"}</div>
              <div className="data-box-content">
                <ul className="list-unstyled">
                  {record.specialtiesDisplay.map((s) => <li key={s}>{s}</li>) }
                </ul>
              </div>
            </div>
          </Accordion>
          <Accordion title="Address / Location" testId="pci-dashboard-provider-profile__address-location-accordion">
            <Waffles<PciDashboardFlyoutAddressTableData>
              columns={addressColumnConfig}
              data={this.state.addressTableData}
              disableStickyHeader={true}
              fullWidth={true}
              hideTableHead={false}
              keyColumns={["type"]}
              testId={`pci-dashboard-provider-profile__address-location-table`}
              theme={WafflesThemes.Simple}
            />
          </Accordion>
          {this.props.enabledFeatureFlags.has(Features.ENABLE_CARE_JOURNEY_SCORES) && this.props.project.showCareJourneyScores && (
            <Accordion title={<><img src={`${window.customUIBaseUrl}/images/carejourney logo.svg`} /></>} testId="pci-dashboard-provider-carejourney-accordion">
              <p>Cost Score: {valueOrDashes(record.careJourneyScoresCostScore)}/5</p>
              <p>Quality Score: {valueOrDashes(record.careJourneyScoresQualityScore)}/5</p>
              <h5>Episode Cost and Quality</h5>
              <Waffles<PciDashboardFlyoutCareJourneyConditionTableData>
                columns={carejourneyColumnConfig}
                data={this.state.carejourneyTableData}
                disableStickyHeader={true}
                fullWidth={true}
                hideTableHead={false}
                keyColumns={["type"]}
                testId={`pci-dashboard-provider-profile__carejourney-location-table`}
                theme={WafflesThemes.Simple}
              />
            </Accordion>
          )}
          <Accordion title="Claims" testId="pci-dashboard-provider-profile__claims-accordion">
            {
              this.props.project.showPciData ? (
                <ClaimsProviderInsights showTricare={this.props.project.showTricareClaimsVolumeColumn} showVA={this.props.project.showVaClaimsVolumeColumn} record={record as unknown as ServicingProviderDetail} />
              ) : (
                <QESAlert classNames="text-transform-none mb-0" type="info" persist={true}>
                  Quest can deliver powerful provider performance insights through the <b>Provider Claims Insights</b> package. Please contact your sales representative. <br /> <br />
                  Insights include: <br />
                  <ul className="mb-0">
                    <li>
                      Provider effectiveness (quality) and efficiency (cost) scores
                    </li>
                    <li>
                      Provider activity by Line of Business
                    </li>
                    <li>
                      Provider payer relationships
                    </li>
                    <li>
                      Provider top conditions and procedures
                    </li>
                  </ul>
                </QESAlert>
              )}
          </Accordion>
        </AccordionContainer>
      </div>
    );
  }

  private renderStatus(npiStatus: NPIStatus) {
    switch (npiStatus) {
      case NPIStatus.Deactivated:
      case NPIStatus.Invalid:
        return (<small className="text-danger pl5"><i className="fa fa-times supress-panel-styles" /> {npiStatus}</small>);
      case NPIStatus.Missing:
      case NPIStatus.Unknown:
        return (<small className="text-warning pl5"><i className="fa fa-question supress-panel-styles" /> {npiStatus}</small>);
      case NPIStatus.Valid:
        return (<small className="text-success pl5"><i className="fa fa-check supress-panel-styles" /> {npiStatus}</small>);
    }
  }

  private renderAttestationStatus(showAttestation: boolean, lastAttestedDate?: Date) {
    if (!showAttestation) {
      return;
    }

    return (
      <>
        <br />
        <strong>Last Attested Date</strong> {lastAttestedDate ? formatUnconvertedDateCustom(lastAttestedDate, "L") : "---"}
      </>
    );
  }

  private createDefaultRecord(record: PciDashboardProvider) {
    record.npi = this.props.npi;
    record.providerId = this.props.providerId;
    record.displayName = this.props.providerName
    record.specialtiesDisplay = [this.props.specialtyGroupCode + " - " + this.props.specialtyGroupName];

    if (!record.addresses || record.addresses.length === 0) {
      record.addresses = [{
        /* eslint-disable sort-keys */
        address: "",
        address2: "",
        city: "",
        countyName: "",
        countySSA: this.props.countySSA,
        state: "",
        zip: "",
        latitude: "",
        longitude: ""
        /* eslint-enable sort-keys */
      }];
    }
  }
}

const PciDashboardProviderFlyout = withFeatureFlagContext(WrappedPciDashboardProviderFlyout)
export default PciDashboardProviderFlyout